import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../services/api';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/layout/PageHeader';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField';
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths } from 'date-fns';

const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
const EMPTY = { title:'', description:'', startDate:'', endDate:'', type:'meeting', color:'#6366f1', location:'', allDay:false };

export function CalendarPage() {
  const [events,setEvents]=useState([]); const [cur,setCur]=useState(new Date()); const [sel,setSel]=useState(null);
  const [open,setOpen]=useState(false); const [editing,setEd]=useState(null); const [form,setForm]=useState(EMPTY); const [saving,setSv]=useState(false);

  const load = async () => {
    try {
      const r = await calendarAPI.getAll({ startDate:format(startOfMonth(cur),'yyyy-MM-dd'), endDate:format(endOfMonth(cur),'yyyy-MM-dd') });
      setEvents(r.data||[]);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(()=>{load();},[cur]);

  const days = eachDayOfInterval({ start:startOfWeek(startOfMonth(cur),{weekStartsOn:1}), end:endOfWeek(endOfMonth(cur),{weekStartsOn:1}) });
  const dayEvents = day => events.filter(e => { try { return isSameDay(new Date(e.startDate),day); } catch { return false; } });

  const openCreate = (day=null) => {
    setEd(null);
    const d = day ? format(day,'yyyy-MM-dd') : '';
    setForm({...EMPTY, startDate:d?d+'T09:00':'', endDate:d?d+'T10:00':''});
    setOpen(true);
  };
  const openEdit = ev => { setEd(ev); setForm({ title:ev.title, description:ev.description||'', type:ev.type, color:ev.color||'#6366f1', location:ev.location||'', allDay:ev.allDay||false, startDate:format(new Date(ev.startDate),"yyyy-MM-dd'T'HH:mm"), endDate:format(new Date(ev.endDate),"yyyy-MM-dd'T'HH:mm") }); setOpen(true); };
  const del = async id => { if(!window.confirm('Delete event?'))return; try{await calendarAPI.delete(id);toast.success('Deleted');load();setSel(null);}catch{toast.error('Failed');} };

  const save = async e => {
    e.preventDefault(); setSv(true);
    try { editing ? await calendarAPI.update(editing._id,form) : await calendarAPI.create(form); toast.success(editing?'Updated':'Created'); setOpen(false); load(); }
    catch(err){toast.error(err.message||'Failed');}
    finally{setSv(false);}
  };

  const s=(k,v)=>setForm(f=>({...f,[k]:v}));

  return (
    <div className="space-y-5">
      <PageHeader title="Calendar" subtitle="Manage events and schedules" actions={<Button icon={PlusIcon} onClick={()=>openCreate()}>New Event</Button>}/>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{format(cur,'MMMM yyyy')}</h2>
          <div className="flex gap-2">
            <button onClick={()=>setCur(subMonths(cur,1))} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeftIcon className="w-5 h-5"/></button>
            <button onClick={()=>setCur(new Date())} className="px-3 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Today</button>
            <button onClick={()=>setCur(addMonths(cur,1))} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRightIcon className="w-5 h-5"/></button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-gray-100">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d=><div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-400 uppercase">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day,i)=>{
            const de=dayEvents(day); const isSel=sel&&isSameDay(day,sel);
            return (
              <div key={i} onClick={()=>setSel(isSel?null:day)} className={'min-h-20 p-1.5 border-b border-r border-gray-50 cursor-pointer transition-colors '+(isSameMonth(day,cur)?isSel?'bg-indigo-50':'hover:bg-gray-50/70':'bg-gray-50/40')}>
                <div className={'w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1 '+(isToday(day)?'bg-indigo-600 text-white':isSameMonth(day,cur)?'text-gray-700':'text-gray-300')}>{format(day,'d')}</div>
                {de.slice(0,2).map((e,j)=><div key={j} onClick={ev=>{ev.stopPropagation();openEdit(e);}} className="text-xs px-1 py-0.5 rounded text-white truncate mb-0.5 cursor-pointer" style={{background:e.color||'#6366f1'}}>{e.title}</div>)}
                {de.length>2&&<p className="text-xs text-gray-400 px-1">+{de.length-2}</p>}
              </div>
            );
          })}
        </div>
        {sel&&(
          <div className="border-t border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">{format(sel,'EEEE, MMMM d')}</h3>
              <Button size="sm" icon={PlusIcon} onClick={()=>openCreate(sel)}>Add Event</Button>
            </div>
            {dayEvents(sel).length===0
              ? <p className="text-gray-400 text-sm">No events today.</p>
              : dayEvents(sel).map(e=>(
                <div key={e._id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors mb-2">
                  <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{background:e.color||'#6366f1'}}/>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-900">{e.title}</p>
                    <p className="text-xs text-gray-400">{format(new Date(e.startDate),'HH:mm')} – {format(new Date(e.endDate),'HH:mm')} · {e.type}</p>
                    {e.location&&<p className="text-xs text-gray-400">📍 {e.location}</p>}
                  </div>
                  <div className="flex gap-2 text-xs"><button onClick={()=>openEdit(e)} className="text-indigo-600 hover:underline">Edit</button><button onClick={()=>del(e._id)} className="text-red-500 hover:underline">Del</button></div>
                </div>
              ))
            }
          </div>
        )}
      </div>
      <Modal isOpen={open} onClose={()=>setOpen(false)} title={editing?'Edit Event':'New Event'}>
        <form onSubmit={save} className="space-y-4">
          <FormField label="Title" required><Input value={form.title} onChange={e=>s('title',e.target.value)} required placeholder="Event title"/></FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start" required><Input type="datetime-local" value={form.startDate} onChange={e=>s('startDate',e.target.value)} required/></FormField>
            <FormField label="End" required><Input type="datetime-local" value={form.endDate} onChange={e=>s('endDate',e.target.value)} required/></FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Type"><Select value={form.type} onChange={e=>s('type',e.target.value)}>{['meeting','deadline','reminder','holiday','task','other'].map(t=><option key={t} value={t}>{t}</option>)}</Select></FormField>
            <FormField label="Color"><div className="flex gap-2 pt-1">{COLORS.map(c=><button key={c} type="button" onClick={()=>s('color',c)} className={'w-7 h-7 rounded-full border-2 transition-all '+(form.color===c?'border-gray-400 scale-110':'border-transparent')} style={{background:c}}/>)}</div></FormField>
          </div>
          <FormField label="Location"><Input value={form.location} onChange={e=>s('location',e.target.value)} placeholder="Room, link, address..."/></FormField>
          <FormField label="Description"><Textarea value={form.description} onChange={e=>s('description',e.target.value)} rows={2}/></FormField>
          <div className="flex gap-3 pt-2"><Button type="submit" loading={saving} className="flex-1">{editing?'Update':'Create'}</Button><Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button></div>
        </form>
      </Modal>
    </div>
  );
}